import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProviderPageLayout from '../../components/ProviderPageLayout';
import { PasswordField, passwordRules } from '../../components/ui/PasswordField';
import { ServiceControl } from '../../components/ui/ServiceControl';
import { getStoreInfo, cacheStoreInfo, getCachedStoreInfo } from '../../services/storeApi';
import { useAuth } from '../../auth/useAuth';

const isPasswordValid = (password) => {
  const r = passwordRules(password);
  return r.length && r.upper && r.lower && r.number && r.special;
};

const ProviderProfile = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut, changePassword } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [store, setStore] = useState(() => getCachedStoreInfo());

  useEffect(() => {
    if (!authLoading && !user) navigate('/provider/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (authLoading || !user) return;
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    getStoreInfo(accessToken)
      .then((info) => {
        if (info) {
          setStore(info);
          cacheStoreInfo(info);
        }
      })
      .catch(() => {
        // Cached / Cognito values still render; the panel just shows less detail.
      });
  }, [authLoading, user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/provider/login');
  };

  const attrs = user?.attributes || {};
  const displayName = `${attrs.given_name || ''} ${attrs.family_name || ''}`.trim();
  const email = attrs.email || '';
  const storeName = store?.storeName || attrs['custom:store_name'] || '';
  const storeAddressLine = [
    store?.storeAddress,
    [store?.storeCity, store?.storeState].filter(Boolean).join(', '),
    store?.storeZip,
  ]
    .filter(Boolean)
    .join(', ');
  const deviceId = store?.deviceId || attrs['custom:device_id'] || '';

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword) {
      setError('Enter your current password.');
      return;
    }
    if (!isPasswordValid(newPassword)) {
      setError('New password does not meet all requirements.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must differ from your current password.');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err.code === 'NotAuthorizedException') {
        setError('Current password is incorrect.');
      } else {
        setError(err.message || 'Could not update password.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPasswordFlow = async () => {
    const confirmed = window.confirm(
      'You will be signed out and receive a reset code by email. Continue?'
    );
    if (!confirmed) return;

    const resetIdentity = email || user?.username || localStorage.getItem('username') || '';
    if (resetIdentity) sessionStorage.setItem('resetUsername', resetIdentity);

    await signOut();
    sessionStorage.removeItem('providerUsername');
    navigate('/provider/forgot', { state: { email: resetIdentity, fromProfile: true } });
  };

  if (authLoading || !user) {
    return (
      <ProviderPageLayout variant="app" wide onSignOut={handleSignOut}>
        <div className="provider-loading">
          <div className="provider-spinner" />
          <span>Loading profile…</span>
        </div>
      </ProviderPageLayout>
    );
  }

  return (
    <ProviderPageLayout variant="app" wide onSignOut={handleSignOut}>
      <div className="provider-page-header">
        <div>
          <h1>Profile</h1>
          <p className="provider-page-meta">Account details and security</p>
        </div>
        <Link to="/provider/" className="provider-btn provider-btn--secondary provider-btn--sm provider-btn--inline">
          ← Dashboard
        </Link>
      </div>

      <div className="provider-panel">
        <h2 className="provider-panel-title">Account</h2>
        <div className="provider-info-grid">
          {displayName && (
            <div className="provider-info-item">
              <label>Name</label>
              <p>{displayName}</p>
            </div>
          )}
          {email && (
            <div className="provider-info-item">
              <label>Email</label>
              <p>{email}</p>
            </div>
          )}
        </div>
      </div>

      <div className="provider-panel">
        <h2 className="provider-panel-title">Store</h2>
        {storeName || storeAddressLine || deviceId ? (
          <div className="provider-info-grid">
            {storeName && (
              <div className="provider-info-item">
                <label>Store name</label>
                <p>{storeName}</p>
              </div>
            )}
            {storeAddressLine && (
              <div className="provider-info-item">
                <label>Address</label>
                <p>{storeAddressLine}</p>
              </div>
            )}
            {store?.storePhone && (
              <div className="provider-info-item">
                <label>Phone</label>
                <p>{store.storePhone}</p>
              </div>
            )}
            {store?.storeCountry && (
              <div className="provider-info-item">
                <label>Country</label>
                <p>{store.storeCountry}</p>
              </div>
            )}
            {deviceId && (
              <div className="provider-info-item">
                <label>Device ID</label>
                <p>{deviceId}</p>
              </div>
            )}
          </div>
        ) : (
          <p style={{ fontSize: 14, color: 'var(--color-text-muted)', margin: 0 }}>
            No store information on file yet.
          </p>
        )}
      </div>

      <div className="provider-panel">
        <h2 className="provider-panel-title">Change password</h2>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', marginBottom: 20 }}>
          Enter your current password to set a new one.
        </p>

        {error && <div className="provider-alert provider-alert--error">{error}</div>}
        {success && <div className="provider-alert provider-alert--success">{success}</div>}

        <form onSubmit={handleChangePassword}>
          <PasswordField
            id="currentPassword"
            label="Current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
          <PasswordField
            id="newPassword"
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            showRules
          />
          <PasswordField
            id="confirmPassword"
            label="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />

          <button
            type="submit"
            disabled={submitting}
            className="provider-btn provider-btn--primary"
            style={{ maxWidth: 280 }}
          >
            {submitting ? 'Updating…' : 'Update password'}
          </button>
        </form>

        <div className="provider-section-divider">
          <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
            Don&apos;t know your current password?
          </p>
          <button
            type="button"
            onClick={handleForgotPasswordFlow}
            className="provider-btn provider-btn--secondary"
            style={{ maxWidth: 320 }}
          >
            Reset via email
          </button>
        </div>
      </div>

      <ServiceControl />
    </ProviderPageLayout>
  );
};

export default ProviderProfile;
